import * as React from 'react';
import { Container } from 'src/shared/ui/layout';
import { AccountSubscriptionFormFields } from './subscriptions';
import { notificationTypeEnum } from 'src/account/my-account/ui/my-account.page';
import { Loading } from 'src/shared';
import { useParams } from 'react-router-dom';
import { breakpoints, useMediaQuery } from 'src/shared/hooks/use-media-query';
import { useAccountSubScriptionInfo } from '../service/account.hooks';

export const UnsubscribePage = () => {
  const { id } = useParams();
  const isMobile = useMediaQuery(
    [breakpoints.mobile, breakpoints.tablet, breakpoints.desktop],
    [true, true, false],
  );
  const { data, loading: dataLoading } = useAccountSubScriptionInfo(id);

  const account = React.useMemo(() => {
    return data?.accountSubscriptionInfo ?? {};
  }, [data]);

  const affiliations = React.useMemo(() => {
    return data?.accountSubscriptionInfo?.affiliations || [];
  }, [data]);

  // Reorganize subscriptions by franchise
  const subscriptionsByFranchiseId = React.useMemo(() => {
    return account?.subscriptions?.reduce((acc, subscription) => {
      if (!subscription.franchiseId) return acc;
      acc[subscription.franchiseId] = acc[subscription.franchiseId] || [];
      acc[subscription.franchiseId] = [
        ...acc[subscription.franchiseId],
        subscription,
      ];
      return acc;
    }, {});
  }, [account]);
  const globalSubscriptionWithNullFranchiseId = React.useMemo(() => {
    const globalNewsOrAnnouncement = account?.subscriptions?.find(
      (subscription) =>
        !subscription.franchiseId &&
        subscription.notificationId === notificationTypeEnum.newsOrAnnouncement,
    );
    const globalClassNotification = account?.subscriptions?.find(
      (subscription) =>
        !subscription.franchiseId &&
        subscription.notificationId === notificationTypeEnum.classNotification,
    );
    if (!globalNewsOrAnnouncement?.id && !globalClassNotification?.id) {
      return null;
    }
    return {
      classNotification: globalClassNotification?.isSmsSubscribedYau,
      emailNewsOrAnnouncement: globalNewsOrAnnouncement?.isEmailSubscribed,
      newsOrAnnouncement: globalNewsOrAnnouncement?.isSmsSubscribedYau,
      emailClassNotification: true,
    };
  }, [account]);

  // Prepare franchisesOptIn array for useFieldArray
  const franchisesOptIn = React.useMemo(() => {
    return Object.keys(subscriptionsByFranchiseId ?? {})
      .map((franchiseId) => {
        const franchiseSubscription = subscriptionsByFranchiseId[franchiseId];
        const newsOrAnnouncement = franchiseSubscription?.find(
          (e) => e.notificationId === notificationTypeEnum.newsOrAnnouncement,
        );
        const classNotification = franchiseSubscription?.find(
          (e) => e.notificationId === notificationTypeEnum.classNotification,
        );
        const franchiseName = affiliations.find(
          ({ id }) => +id === +franchiseId,
        )?.name;
        if (!franchiseName) {
          return null;
        }
        return {
          id: franchiseId,
          name: franchiseName,
          classNotification: Boolean(classNotification?.isSmsSubscribedYau),
          emailClassNotification: true,
          newsOrAnnouncement: Boolean(newsOrAnnouncement?.isSmsSubscribedYau),
          emailNewsOrAnnouncement: Boolean(
            newsOrAnnouncement?.isEmailSubscribed,
          ),
        };
      })
      .filter(Boolean);
  }, [subscriptionsByFranchiseId, affiliations]);

  // Calculate global notification states based on franchises
  const globalNotificationStates = React.useMemo(() => {
    if (!franchisesOptIn.length) {
      return globalSubscriptionWithNullFranchiseId;
    }
    return {
      classNotification: franchisesOptIn.every((f) => f.classNotification),
      emailClassNotification: true,
      newsOrAnnouncement: franchisesOptIn.every((f) => f.newsOrAnnouncement),
      emailNewsOrAnnouncement: franchisesOptIn.every(
        (f) => f.emailNewsOrAnnouncement,
      ),
    };
  }, [franchisesOptIn, globalSubscriptionWithNullFranchiseId]);

  // Initialize form with default values
  const defaultValues = React.useMemo(
    () => ({
      ...globalNotificationStates,
      franchisesOptIn,
    }),
    [globalNotificationStates, franchisesOptIn],
  );

  return (
    <Container {...(isMobile ? { $width: '360px' } : {})}>
      <h1 className="text-center mb-32">Manage subscriptions</h1>
      {dataLoading && <Loading className="absolute" />}
      {!dataLoading && (
        <AccountSubscriptionFormFields
          account={account}
          isLoading={dataLoading}
          defaultValues={defaultValues}
        />
      )}
    </Container>
  );
};
